import React, { useRef, useState, useEffect } from "react";
import "./expertCarousel.css";
import { Link } from "react-router-dom";
import { getDataBS } from "../../utils/awsService";

const Carousel = () => {
  const sliderTrackRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch experts from database
  useEffect(() => {
    const fetchExperts = async () => {
      try {
        setLoading(true);
  
        const responseData = await getDataBS("/expert_table");
  
        if (responseData && responseData.data) {
          let expertData;
  
          if (responseData.data.data && Array.isArray(responseData.data.data)) {
            expertData = responseData.data.data;
          } else if (Array.isArray(responseData.data)) {
            expertData = responseData.data;
          } else {
            throw new Error("Unexpected data format");
          }
  
          const processedExperts = expertData.map((item) => {
            const expert = item.expert || item;
            return {
              id: expert.id || null,
              originalId: expert.originalId,
              name: expert.name || "Unnamed",
              position: expert.position || "Counselor",
              imageSrc:
                expert.imageSrc ||
                "https://via.placeholder.com/400x400?text=No+Image",
              certifications: expert.certifications || "",
              expertise: expert.expertise || "General Counseling",
              experience: expert.experience || "Not specified",
              uploadedAt: expert.uploadedAt || new Date().toISOString(),
            };
          });
  
          const sortedExperts = processedExperts.sort((a, b) => {
            const aId = a.originalId
              ? Number(a.originalId) || a.originalId
              : null;
            const bId = b.originalId
              ? Number(b.originalId) || b.originalId
              : null;
  
            if (typeof aId === "number" && typeof bId === "number") {
              return aId - bId;
            }
  
            if (a.originalId && b.originalId) {
              return String(a.originalId).localeCompare(String(b.originalId));
            }
  
            return a.id && b.id ? String(a.id).localeCompare(String(b.id)) : 0;
          });
  
          setExperts(sortedExperts);
        } else {
          throw new Error("Received invalid experts data format");
        }
      } catch (err) {
        console.error("Error fetching experts:", err);
        setError("Failed to load experts. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
  
    fetchExperts();
  }, []); 
  

  // Mouse event handlers for drag scrolling
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX);
    setScrollLeft(sliderTrackRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();

    // Calculate the distance moved
    const x = e.pageX;
    const delta = x - startX;

    // Apply the scroll - negative delta scrolls right, positive delta scrolls left
    sliderTrackRef.current.scrollLeft = scrollLeft - delta;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
    setScrollLeft(sliderTrackRef.current.scrollLeft);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;

    // Calculate the distance moved
    const x = e.touches[0].clientX;
    const delta = x - startX;

    // Apply the scroll
    sliderTrackRef.current.scrollLeft = scrollLeft - delta;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Create duplicate experts to create an infinite scrolling effect
  const duplicate_experts = experts.length > 0 ? [...experts, ...experts, ...experts, ...experts] : [];

  if (loading) {
    return (
      <div className="expert-carousel" id="experts">
        <center>
          <h1 className="experts-title">Meet Our Experts</h1>
          <div className="loading-spinner">Loading experts...</div>
        </center>
      </div>
    );
  }

  if (error) {
    return (
      <div className="expert-carousel" id="experts">
        <center>
          <h1 className="experts-title">Meet Our Experts</h1>
          <div className="error-message">{error}</div>
        </center>
      </div>
    );
  }

  if (experts.length === 0) {
    return (
      <div className="expert-carousel" id="experts">
        <center>
          <h1 className="experts-title">Meet Our Experts</h1>
          <p>No experts available at the moment. Please check back later.</p>
        </center>
      </div>
    );
  }

  return (
    <div className="expert-carousel" id="experts">
      <center>
        <h1 className="experts-title">Meet Our Experts</h1>
      </center>
      <div
        className="slider-container"
        ref={sliderTrackRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="slider-track">
          {duplicate_experts.map((expert, index) => (
            <div className="slide" key={`${expert.id}-${index}`}>
              <Link to={`/expert/${expert.id}`} className="card-link">
                <div className="card">
                  <div className="card-image">
                    <img src={expert.imageSrc} alt={`${expert.name}`} />
                  </div>

                  <div className="card-content">
                    <h2 className="card-title">{expert.name}</h2>
                    <p className="card-position">{expert.position}</p>

                    {expert.certifications &&
                      expert.certifications.trim() !== "" && (
                        <div className="card-certifications">
                          <span>Certifications:</span> {expert.certifications}
                        </div>
                      )}

                    <div className="card-expertise">
                      <span>Areas of Expertise:</span> {expert.expertise}
                    </div>

                    <div className="card-experience">
                      <span>Years of Experience:</span> {expert.experience}
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
      <div className="scroll-instruction">
        <div className="scroll-arrows">
          <span className="arrow-left"></span>
          <span className="arrow-left"></span>
        </div>
        Swipe
        <div className="scroll-arrows">
          <span className="arrow-right"></span>
          <span className="arrow-right"></span>
        </div>
      </div>
    </div>
  );
};

export default Carousel;
